import axios from "axios";

export interface FormAnswer {
  controlType: string;
  dataType: string;
  question: string;
  values: string[];
  labelFullPath: {
    path: string;
  };
}

export interface FormSubmission {
  name: string;
  identifier: number;
  answers: FormAnswer[];
}

export interface FormField {
  id: number;
  field: string;
  value: string;
  path: string;
  controlType: string;
  dataType: string;
  question: string;
}

export interface Form {
  id: string;
  name: string;
  fields: FormField[];
  createdAt: string;
  updatedAt: string;
  status: string;
  statusComments: string;
  homeForemanAreaId?: number;
  statusRequestedBy?: {
    id: number;
    name: string;
    lastName: string;
    email: string;
    role?: { name: string } | null;
  };
  formHistory?: FormHistory[];
  processingResult?: {
    success: boolean;
    createdTasks: Array<{
      taskId: number;
      taskNumber: string;
      taskType: string;
      activityName?: string;
      assetName?: string;
      contractArea?: string;
      efforts?: Array<{
        type: string;
        quantity: number;
        rate: number;
        description: string;
        employeeName: string;
      }>;
    }>;
    errors: string[];
  };
}

/** HE-F21 - the vendor a submission's PDF would be emailed to. */
export interface FormVendorContact {
  vendorName: string | null;
  contactEmail: string | null;
  /** False when vendor email is switched off for this environment. */
  emailEnabled: boolean;
}

export interface FormHistory {
  id: number;
  formId: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  comments?: string;
  stage: string;
  external: boolean;
  userId?: number;
  createdAt: string;
  user?: {
    id: number;
    name: string;
    lastName: string;
  };
}

interface FormHistoryComment {
  comments: string;
  userId: number;
  isExternal: boolean;
}

export interface UnapprovedSubmissionCheck {
  count: number;
  submissions: {
    id: string;
    name: string;
    status: string;
    createdAt: string;
    taskFieldValue: string | null;
    statusRequestedBy: {
      id: number;
      name: string;
      lastName: string;
      email: string;
    } | null;
  }[];
}

export interface PaginatedFormsResponse {
  data: Form[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

export interface FormQueryParams {
  page?: number;
  limit?: number;
  status?: string;
  formType?: string;
  serviceArea?: string;
  foremanArea?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  employeeName?: string;
  subcontractorName?: string;
}

export interface FormFilterOptions {
  serviceAreas: Array<{ id: number; name: string }>;
  foremanAreas: Array<{ 
    id: number; 
    name: string;
    serviceAreaId: number;
    serviceAreaName: string;
  }>;
  statuses: string[];
  employeeNames: string[];
  subcontractorNames: string[];
}

export interface FormFilterCounts {
  statusCounts: {
    all: number;
    pending: number;
    approved: number;
    'need revisions': number;
    declined: number;
    'revisions completed': number;
  };
  formTypeCounts: {
    all: number;
    'Daily Log': number;
    'Hired Equipment': number;
  };
}

export interface DuplicateFormWarning {
  error: string;
  message: string;
  duplicateForms: Array<{
    id: string;
    name: string;
    createdAt: string;
    employeeName: string;
    formDate: string;
    startTime: string;
    duration: string;
  }>;
  requiresConfirmation: boolean;
}

class FormService {
  async submitForm(submission: FormSubmission): Promise<Form> {
    // Moved off /external-api: that surface now requires a shared secret, which a browser
    // cannot send. /api/forms/submit reaches the same handler with session auth.
    const response = await axios.post<Form>(`/api/forms/submit`, submission);
    return response.data;
  }

  async getForms(): Promise<Form[]> {
    const response = await fetch('/api/forms', {
      headers: { 'Content-Type': 'application/json' }
    });
    const data = await response.json();
    
    // Handle both old format (array) and new format (paginated object)
    if (Array.isArray(data)) {
      // Old format - direct array
      return data;
    } else {
      // New format - paginated response
      return data.data || [];
    }
  }

  async getFormsPaginated(params: FormQueryParams = {}, signal?: AbortSignal): Promise<PaginatedFormsResponse> {
    const queryParams = new URLSearchParams();
    
    // Add pagination parameters
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());
    
    // Add filter parameters
    if (params.status) queryParams.append('status', params.status);
    if (params.formType) queryParams.append('formType', params.formType);
    if (params.serviceArea) queryParams.append('serviceArea', params.serviceArea);
    if (params.foremanArea) queryParams.append('foremanArea', params.foremanArea);
    if (params.search) queryParams.append('search', params.search);
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params.employeeName) queryParams.append('employeeName', params.employeeName);
    if (params.subcontractorName) queryParams.append('subcontractorName', params.subcontractorName);
    
    const url = `/api/forms?${queryParams.toString()}`;
    
    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      ...(signal && { signal })
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch forms: ${response.statusText}`);
    }
    
    return response.json();
  }

  async getFilterOptions(): Promise<FormFilterOptions> {
    const response = await fetch('/api/forms/filter-options', {
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch filter options: ${response.statusText}`);
    }
    
    return response.json();
  }

  async getForm(id: string): Promise<Form> {
    try {
      const response = await axios.get<Form>(`/api/forms/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching form:', error);
      throw error;
    }
  }

  async updateFormField(formId: string, fieldId: number, value: string): Promise<Form> {
    const response = await fetch(`/api/forms/${formId}/fields/${fieldId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ value })
    });

    if (!response.ok) {
      throw new Error('Failed to update form field');
    }

    return response.json();
  }

  /** HE-F21 - who a vendor PDF would go to, resolved server-side from the submission. */
  async getFormVendorContact(formId: string): Promise<FormVendorContact> {
    const response = await axios.get<FormVendorContact>(`/api/forms/${formId}/vendor-contact`);
    return response.data;
  }

  async approveForm(
    formId: string,
    confirmDuplicate?: boolean,
    options?: { emailVendorPdf?: boolean; vendorEmail?: { to: string; subject: string; bodyText: string } }
  ): Promise<Form> {
    const response = await axios.post<Form>(`/api/forms/${formId}/approve`, {
      confirmDuplicate,
      emailVendorPdf: options?.emailVendorPdf === true,
      vendorEmail: options?.vendorEmail,
    });
    return response.data;
  }

  async rejectForm(formId: string, comment: string): Promise<Form> {
    const response = await fetch(`/api/forms/${formId}/reject`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ statusComments: comment }),
    });
    const rawText = await response.text().catch(() => '');
    if (!response.ok) {
      let message = 'Failed to reject form';
      if (rawText) {
        try {
          const body = JSON.parse(rawText) as {
            error?: string;
            details?: string;
            code?: string;
          };
          const parts = [body?.error, body?.details].filter(Boolean) as string[];
          message = parts.length > 0 ? parts.join(' ') : message;
          if (body?.code) {
            message = `${message} (${body.code})`;
          }
        } catch {
          message = rawText || response.statusText || message;
        }
      } else if (response.statusText) {
        message = response.statusText;
      }
      throw new Error(message);
    }
    return rawText ? (JSON.parse(rawText) as Form) : ({} as Form);
  }

  async declineForm(formId: string, comment: string): Promise<Form> {
    const response = await fetch(`/api/forms/${formId}/decline`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ statusComments: comment }),
    });
    if (!response.ok) throw new Error('Failed to decline form');
    return response.json();
  }

  async withdrawDecline(formId: string): Promise<Form> {
    const response = await fetch(`/api/forms/${formId}/withdraw-decline`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      const rawText = await response.text().catch(() => '');
      let message = 'Failed to withdraw decline';
      if (rawText) {
        try {
          const body = JSON.parse(rawText);
          message = body?.error || rawText;
        } catch (_) {
          message = rawText;
        }
      } else if (response.statusText) {
        message = response.statusText;
      }
      throw new Error(message);
    }
    return response.json();
  }

  async withdrawApproval(formId: string): Promise<Form> {
    const response = await fetch(`/api/forms/${formId}/withdraw-approval`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      // Try to extract a meaningful message from JSON or plain text
      const rawText = await response.text().catch(() => '');
      let message = 'Failed to withdraw approval';
      if (rawText) {
        try {
          const body = JSON.parse(rawText);
          message = body?.error || rawText;
        } catch (_) {
          message = rawText; // plain text from server
        }
      } else if (response.statusText) {
        message = response.statusText;
      }
      throw new Error(message);
    }
    return response.json();
  }

  async withdrawRevisions(formId: string): Promise<Form> {
    const response = await fetch(`/api/forms/${formId}/withdraw-revisions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) throw new Error('Failed to withdraw revisions');
    return response.json();
  }

  async getFormHistory(formId: string): Promise<FormHistory[]> {
    try {
      const response = await axios.get<FormHistory[]>(`/api/forms/${formId}/history`);
      return response.data;
    } catch (error) {
      console.error('Error fetching form history:', error);
      throw error;
    }
  }

  async addHistoryComment(formId: string, comment: FormHistoryComment): Promise<Form> {
    try {
      const response = await axios.post<Form>(`/api/forms/${formId}/history`, comment);
      return response.data;
    } catch (error) {
      console.error('Error adding form history comment:', error);
      throw error;
    }
  }

  async checkUnapprovedSubmissions(taskId: number): Promise<UnapprovedSubmissionCheck> {
    const response = await fetch(`/api/forms/task/${taskId}/unapproved-submissions`);
    
    if (!response.ok) {
      throw new Error('Failed to check unapproved submissions');
    }
    
    return response.json();
  }

  async getFilterCounts(params: Omit<FormQueryParams, 'page' | 'limit'> = {}): Promise<FormFilterCounts> {
    const queryParams = new URLSearchParams();
    
    // Add filter parameters (excluding pagination)
    if (params.formType) queryParams.append('formType', params.formType);
    if (params.serviceArea) queryParams.append('serviceArea', params.serviceArea);
    if (params.foremanArea) queryParams.append('foremanArea', params.foremanArea);
    if (params.search) queryParams.append('search', params.search);
    if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom);
    if (params.dateTo) queryParams.append('dateTo', params.dateTo);
    if (params.employeeName) queryParams.append('employeeName', params.employeeName);
    if (params.subcontractorName) queryParams.append('subcontractorName', params.subcontractorName);
    
    const url = `/api/forms/filter-counts?${queryParams.toString()}`;
    
    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch filter counts: ${response.statusText}`);
    }
    
    return response.json();
  }
}

export const formService = new FormService();
